import { getCategoryById, productSidebar, findProduct as findBuiltInProduct } from '../data/productCatalog'

/** 与 UniApp 端共用 storage key，便于同源部署时数据一致 */
export const STORAGE_KEY = 'custom_products_v1'

export const SHELF_CATEGORIES = [
  { id: 'software', groupId: 'system', label: '系统类' },
  { id: 'smart-line', groupId: 'education', label: '教育类' },
  { id: 'humanoid', groupId: 'robotics', label: '机器人类' },
  { id: 'agv', groupId: 'agv-group', label: 'AGV类' },
  { id: 'automation-line', groupId: 'automation', label: '非标自动化类' },
]

const APP_CATEGORY_MAP = {
  system: 'software',
  'teaching-equipment': 'smart-line',
  robotics: 'humanoid',
  agv: 'agv',
  automation: 'automation-line',
}

function slugify(text) {
  return (
    String(text || '')
      .trim()
      .toLowerCase()
      .replace(/[^\w\u4e00-\u9fa5]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'product'
  )
}

function normalizeRawItem(raw) {
  if (!raw || typeof raw !== 'object') return null

  let categoryId = raw.categoryId
  if (!categoryId && raw.category) {
    categoryId = APP_CATEGORY_MAP[raw.category] || 'software'
  }
  if (!categoryId) categoryId = 'software'

  const highlights = Array.isArray(raw.highlights)
    ? raw.highlights
    : String(raw.highlightsText || '')
        .split(/[,，、\n]/)
        .map((s) => s.trim())
        .filter(Boolean)

  const specs = Array.isArray(raw.specs)
    ? raw.specs
    : String(raw.specsText || '')
        .split(/[,，、\n]/)
        .map((s) => s.trim())
        .filter(Boolean)

  const summary = (raw.summary || raw.briefIntro || '').trim()
  const description = (raw.description || summary).trim()
  const detailContent = (raw.detailContent || '').trim()
  const introParagraphs = raw.introParagraphs?.length
    ? raw.introParagraphs
    : detailContent
      ? detailContent.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean)
      : []

  const id = raw.id || `custom-${slugify(raw.name)}-${Date.now()}`
  const slug = raw.slug || `custom-${slugify(raw.name)}-${String(id).slice(-6)}`

  return {
    id,
    slug,
    categoryId,
    name: String(raw.name || '').trim(),
    summary,
    description,
    briefIntro: (raw.briefIntro || summary).trim(),
    image: raw.image || '',
    gallery: raw.gallery?.length ? raw.gallery : raw.image ? [raw.image] : [],
    highlights,
    specs,
    detailContent,
    introParagraphs,
    isCustomProduct: true,
    createdAt: raw.createdAt || Date.now(),
  }
}

export function loadCustomProductsRaw() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const list = JSON.parse(raw)
    if (!Array.isArray(list)) return []
    return list.map(normalizeRawItem).filter(Boolean)
  } catch {
    return []
  }
}

export function saveCustomProductsRaw(list) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
}

export function toGaorenProduct(raw) {
  const item = normalizeRawItem(raw)
  if (!item) return null

  return {
    slug: item.slug,
    name: item.name,
    desc: item.summary,
    model: item.specs[0] || '用户上架',
    image: item.image || '/images/brochure/img-p9-5.jpeg',
    tag: '上架产品',
    tagline: item.summary,
    load: item.specs[1] || '定制交付',
    intro: item.description,
    introPoints: item.introParagraphs.length ? item.introParagraphs : item.highlights,
    detailPoints: item.highlights.length ? item.highlights : [item.description],
    gallery: item.gallery,
    specGroups: item.specs.length
      ? [{ name: '产品规格', rows: item.specs.map((s, i) => [`规格 ${i + 1}`, s]) }]
      : [
          {
            name: '产品信息',
            rows: [
              ['产品名称', item.name],
              ['简介', item.summary],
            ],
          },
        ],
    isCustomProduct: true,
    customId: item.id,
  }
}

export function getCustomProducts() {
  return loadCustomProductsRaw()
}

export function getCustomProductsForCategory(categoryId) {
  return loadCustomProductsRaw()
    .filter((p) => p.categoryId === categoryId)
    .map(toGaorenProduct)
    .filter(Boolean)
}

export function findCustomProduct(categoryId, productSlug) {
  const raw = loadCustomProductsRaw().find(
    (p) => p.categoryId === categoryId && (p.slug === productSlug || p.id === productSlug)
  )
  if (!raw) return null
  const category = getCategoryById(categoryId)
  return { category, product: toGaorenProduct(raw) }
}

export function findCustomProductBySlug(productSlug) {
  const raw = loadCustomProductsRaw().find((p) => p.slug === productSlug || p.id === productSlug)
  if (!raw) return null
  const category = getCategoryById(raw.categoryId)
  return { category, product: toGaorenProduct(raw), raw }
}

export function buildCustomProductDraft(form) {
  const categoryId = form.categoryId || SHELF_CATEGORIES[0].id
  const id = form.id || `custom-${slugify(form.name)}-${Date.now()}`
  const slug = form.slug || `custom-${slugify(form.name)}-${String(id).slice(-6)}`

  const highlights = String(form.highlightsText || '')
    .split(/[,，、\n]/)
    .map((s) => s.trim())
    .filter(Boolean)

  const specs = String(form.specsText || '')
    .split(/[,，、\n]/)
    .map((s) => s.trim())
    .filter(Boolean)

  const summary = String(form.summary || '').trim()
  const detailContent = String(form.detailContent || '').trim()

  return normalizeRawItem({
    id,
    slug,
    categoryId,
    name: String(form.name || '').trim(),
    summary,
    description: String(form.description || summary).trim(),
    briefIntro: String(form.briefIntro || summary).trim(),
    image: String(form.image || '').trim(),
    highlights,
    specs,
    detailContent,
    createdAt: form.createdAt || Date.now(),
  })
}

export function addCustomProduct(form) {
  const product = buildCustomProductDraft(form)
  const list = loadCustomProductsRaw()
  list.unshift(product)
  saveCustomProductsRaw(list)
  return product
}

export function updateCustomProduct(id, form) {
  const list = loadCustomProductsRaw()
  const index = list.findIndex((p) => p.id === id)
  if (index < 0) return null
  const prev = list[index]
  const next = buildCustomProductDraft({ ...form, id, slug: prev.slug, createdAt: prev.createdAt })
  list[index] = next
  saveCustomProductsRaw(list)
  return next
}

export function removeCustomProduct(id) {
  saveCustomProductsRaw(loadCustomProductsRaw().filter((p) => p.id !== id))
}

export function getShelfCategoryLabel(categoryId) {
  return SHELF_CATEGORIES.find((c) => c.id === categoryId)?.label || findCategoryLabelFallback(categoryId)
}

function findCategoryLabelFallback(categoryId) {
  for (const group of productSidebar) {
    const child = group.children.find((c) => c.id === categoryId)
    if (child) return child.label
  }
  return '自定义分类'
}

export function findProduct(categoryId, productSlug) {
  return findCustomProduct(categoryId, productSlug) || findBuiltInProduct(categoryId, productSlug)
}

export function getMergedCategoryProducts(categoryId) {
  const category = getCategoryById(categoryId)
  const custom = getCustomProductsForCategory(categoryId)
  return [...(category.products || []), ...custom]
}

export function getRelatedCustomProducts(categoryId, productSlug, limit = 4) {
  const pool = getMergedCategoryProducts(categoryId).filter((p) => p.slug !== productSlug)
  return pool.slice(0, limit)
}

export function searchCustomProducts(query, limit = 12) {
  const q = String(query || '').trim().toLowerCase()
  if (!q) return []

  const results = []
  for (const raw of loadCustomProductsRaw()) {
    const category = getCategoryById(raw.categoryId)
    const haystack = [raw.name, raw.summary, raw.description, category.label]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
    if (haystack.includes(q)) {
      results.push({
        name: raw.name,
        desc: raw.summary,
        image: raw.image,
        categoryId: raw.categoryId,
        categoryLabel: category.label,
        groupLabel: category.parentLabel,
        slug: raw.slug,
      })
    }
  }
  return results.slice(0, limit)
}

export function readImageFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}
